// Source-type switcher for the manual link picker (W53.1 D-1).
//
// The backend has accepted all four link sources since W2. Until this existed
// the UI offered one of them, because `CalendarEventDetail.tsx` passed the
// literal `"album"` to `linkEvent`. One sheet shell, four data sources —
// four separate sheets would have meant four copies of the same chrome, four
// empty states, and four places for the list behind them to drift.

import { BottomSheet } from "@/components/patterns/BottomSheet";
import { LinkTargetList } from "@/components/calendar/LinkTargetList";
import {
  LINK_SOURCE_LABEL,
  LINK_SOURCE_TYPES,
  type LinkSourceType,
} from "@/lib/i18n/calendar";
import { cn } from "@/lib/utils/cn";
import { useState } from "react";

export interface EventLinkPickerSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (type: LinkSourceType, id: string) => void;
  busy: boolean;
}

export function EventLinkPickerSheet({
  open,
  onOpenChange,
  onPick,
  busy,
}: EventLinkPickerSheetProps) {
  // Albums first: it was the only tab that existed, so it stays the default and
  // the sheet looks the same as it did before this change.
  const [type, setType] = useState<LinkSourceType>("album");

  return (
    <BottomSheet
      open={open}
      onOpenChange={onOpenChange}
      variant="mobile"
      title="關聯一筆記錄"
      className="max-h-[70vh] overflow-y-auto"
    >
      {/* On an inner element: a hyphenated attribute on a custom component is
          accepted by TypeScript without checking and then dropped by React, so
          `data-testid` on <BottomSheet> never reaches the DOM. */}
      <div data-testid="event-link-picker">
        <div
          role="tablist"
          aria-label="關聯類型"
          className="mb-s-3 flex gap-s-1 overflow-x-auto"
          data-testid="link-source-tabs"
        >
          {LINK_SOURCE_TYPES.map((candidate) => (
            <button
              key={candidate}
              type="button"
              role="tab"
              aria-selected={candidate === type}
              onClick={() => setType(candidate)}
              // 44px min touch height (mobile-components skill / HIG), and the
              // selected tab is carried by weight + fill rather than colour
              // alone so it survives a monochrome check.
              className={cn(
                "min-h-[44px] shrink-0 rounded-full px-s-4 text-body",
                candidate === type
                  ? "bg-ink-1 text-paper font-medium"
                  : "bg-paper-deep text-ink-2 hover:bg-paper-calm",
              )}
              data-testid={`link-source-tab-${candidate}`}
            >
              {LINK_SOURCE_LABEL[candidate]}
            </button>
          ))}
        </div>

        <LinkTargetList
          type={type}
          enabled={open}
          onPick={(id) => onPick(type, id)}
          busy={busy}
        />
      </div>
    </BottomSheet>
  );
}
